import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChatCircleDots, X, PaperPlaneRight, ShieldPlus, EnvelopeSimple, DeviceMobile, ArrowLeft,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { guestApi, formatApiErrorDetail } from "@/lib/api";
import { FaqsCard, HospitalsCard, EHealthCard, PolicyCard, ClaimsCard, DependentsCard, DocumentsCard } from "@/components/insurehub/ChatCards";
import { InsuranceEnquiryForm } from "@/components/insurehub/InsuranceEnquiryForm";
import { CashlessCheckForm } from "@/components/insurehub/CashlessCheckForm";

const GUEST_QUICK = [
  { label: "Insurance enquiry", text: "I want an insurance enquiry" },
  { label: "Cashless check", text: "Check if a hospital is cashless covered" },
  { label: "FAQs", text: "Show me frequently asked questions" },
  { label: "Find hospitals", text: "Find network hospitals near me" },
  { label: "How claims work", text: "How does the claims process work?" },
];

function nowTime() {
  return new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function MiniCard({ card }) {
  if (!card) return null;
  switch (card.type) {
    case "faqs": return <FaqsCard data={card.data} />;
    case "hospitals": return <HospitalsCard data={card.data} />;
    case "ecard": return <EHealthCard data={card.data} />;
    case "policy": return <PolicyCard data={card.data} />;
    case "claims": return <ClaimsCard data={card.data} />;
    case "dependents": return <DependentsCard data={card.data} />;
    case "documents": return <DocumentsCard data={card.data} />;
    case "insurance_enquiry": return <InsuranceEnquiryForm />;
    case "cashless_check": return <CashlessCheckForm />;
    default: return null;
  }
}

export default function GuestChatWidget() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("gate"); // gate | otp | chat
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, typing]);

  const requestOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/otp/request", { email: email.trim(), mobile: mobile.trim(), purpose: "guest_chat" });
      if (data.demo_code) {
        toast.info(`Demo mode: your code is ${data.demo_code}`, { duration: 8000 });
        setCode(data.demo_code);
      } else {
        toast.success("We've emailed you a 6-digit code.");
      }
      setStep("otp");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send code");
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/otp/verify", { email: email.trim(), mobile: mobile.trim(), code: code.trim(), purpose: "guest_chat" });
      if (!data.guest) {
        // Matched a real member — sign them into the full app
        localStorage.setItem("insurehub_token", data.token);
        toast.success(`Welcome, ${data.user.name}! Opening your account…`);
        window.location.href = data.user.role === "admin" || data.user.role === "master_admin" ? "/admin" : "/chat";
        return;
      }
      setToken(data.token);
      setStep("chat");
      setMessages([{
        id: "w", role: "assistant",
        text: "Hi! 👋 I'm the Aarogya Assist Chatbot. I can answer general questions about group medical insurance, share FAQs and help you find network hospitals. For personal policy details, log in with your registered email & mobile.",
        time: nowTime(),
      }]);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Invalid code");
    } finally {
      setLoading(false);
    }
  };

  const send = async (text) => {
    const t = text.trim();
    if (!t || typing || !token) return;
    setInput("");
    setMessages((m) => [...m, { id: `u${Date.now()}`, role: "user", text: t, time: nowTime() }]);
    setTyping(true);
    try {
      const { data } = await guestApi(token).post("/chat/message", { session_id: sessionId, text: t });
      if (data.session_id) setSessionId(data.session_id);
      setMessages((m) => [...m, { id: `a${Date.now()}`, role: "assistant", text: data.reply, card: data.card, time: nowTime() }]);
    } catch (err) {
      setMessages((m) => [...m, { id: `e${Date.now()}`, role: "assistant", text: "Something went wrong. Please try again.", time: nowTime() }]);
    } finally {
      setTyping(false);
    }
  };

  return (
    <>
      {/* Floating button */}
      <button
        data-testid="guest-chat-fab"
        onClick={() => setOpen(true)}
        className={`fixed bottom-6 right-6 z-50 w-16 h-16 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white shadow-[0_10px_30px_rgba(6,78,59,0.4)] flex items-center justify-center transition-[transform,background-color] hover:scale-105 ${open ? "hidden" : "flex"}`}
        aria-label="Open chat"
      >
        <ChatCircleDots size={30} weight="fill" />
        <span className="absolute top-0 right-0 w-4 h-4 rounded-full bg-red-500 border-2 border-white animate-pulse" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ duration: 0.25 }}
            data-testid="guest-chat-panel"
            className="fixed bottom-6 right-6 z-50 w-[calc(100vw-3rem)] sm:w-[400px] h-[600px] max-h-[calc(100vh-3rem)] rounded-3xl bg-white shadow-[0_20px_60px_rgba(0,0,0,0.25)] border border-zinc-200 flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="bg-emerald-950 text-white px-4 py-3 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-full bg-white overflow-hidden ring-1 ring-white/20">
                  <img src="/logo.png" alt="Aarogya Assist" className="w-full h-full object-cover scale-110" />
                </div>
                <div>
                  <p className="font-heading font-semibold leading-tight text-sm">Aarogya Assist Chatbot</p>
                  <p className="text-[11px] text-emerald-300">{step === "chat" ? "online" : "quick help"}</p>
                </div>
              </div>
              <button data-testid="guest-chat-close" onClick={() => setOpen(false)} className="p-1.5 rounded-full hover:bg-white/10 transition-colors">
                <X size={20} />
              </button>
            </div>

            {/* GATE */}
            {step === "gate" && (
              <div className="flex-1 flex flex-col justify-center px-6 grain bg-emerald-50/40">
                <div className="text-center mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                    <ShieldPlus size={30} weight="duotone" className="text-emerald-700" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-emerald-950">Chat with us</h3>
                  <p className="text-sm text-zinc-500 mt-1">Verify your email & mobile to start. If you're an enrolled member, we'll load your policy automatically.</p>
                </div>
                <form onSubmit={requestOtp} className="space-y-3">
                  <div className="relative">
                    <EnvelopeSimple size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                    <input data-testid="guest-email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="Email address" className="w-full rounded-xl border border-zinc-200 pl-10 pr-3 py-3 text-sm outline-none focus:ring-2 focus:ring-emerald-500" />
                  </div>
                  <div className="relative">
                    <DeviceMobile size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                    <input data-testid="guest-mobile" required value={mobile} onChange={(e) => setMobile(e.target.value)}
                      placeholder="Mobile number" className="w-full rounded-xl border border-zinc-200 pl-10 pr-3 py-3 text-sm outline-none focus:ring-2 focus:ring-emerald-500" />
                  </div>
                  <button type="submit" disabled={loading} data-testid="guest-send-otp"
                    className="w-full rounded-full bg-emerald-900 hover:bg-emerald-800 text-white font-medium py-3 transition-colors disabled:opacity-50">
                    {loading ? "Sending…" : "Send verification code"}
                  </button>
                </form>
              </div>
            )}

            {/* OTP */}
            {step === "otp" && (
              <div className="flex-1 flex flex-col justify-center px-6 grain bg-emerald-50/40">
                <button onClick={() => setStep("gate")} className="flex items-center gap-1 text-sm text-zinc-500 mb-4 hover:text-emerald-800 transition-colors">
                  <ArrowLeft size={16} /> Back
                </button>
                <h3 className="font-heading text-xl font-semibold text-emerald-950">Enter the code</h3>
                <p className="text-sm text-zinc-500 mt-1 mb-5">We sent a 6-digit code to <span className="font-medium text-emerald-800">{email}</span></p>
                <form onSubmit={verifyOtp} className="space-y-3">
                  <input data-testid="guest-otp" required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="______" maxLength={6}
                    className="w-full text-center tracking-[0.6em] font-mono text-2xl rounded-xl border border-zinc-200 py-3 outline-none focus:ring-2 focus:ring-emerald-500" />
                  <button type="submit" disabled={loading} data-testid="guest-verify-otp"
                    className="w-full rounded-full bg-emerald-900 hover:bg-emerald-800 text-white font-medium py-3 transition-colors disabled:opacity-50">
                    {loading ? "Verifying…" : "Verify & start chat"}
                  </button>
                  <button type="button" onClick={requestOtp} className="w-full text-xs text-emerald-700 hover:underline">Resend code</button>
                </form>
              </div>
            )}

            {/* CHAT */}
            {step === "chat" && (
              <>
                <div ref={scrollRef} className="flex-1 overflow-y-auto chat-bg px-3 py-4" data-testid="guest-messages">
                  <div className="flex flex-col gap-2.5">
                    {messages.map((m) => (
                      <div key={m.id} className={`flex flex-col gap-2 ${m.role === "user" ? "items-end" : "items-start"}`}>
                        {m.text && (
                          <div className={`max-w-[85%] px-3.5 py-2 text-sm leading-relaxed whitespace-pre-wrap shadow-sm ${m.role === "user" ? "bg-emerald-900 text-white rounded-2xl rounded-br-sm" : "bg-white text-zinc-800 border border-zinc-200 rounded-2xl rounded-bl-sm"}`}>
                            {m.text}
                          </div>
                        )}
                        {m.card && <div className="max-w-[90%]"><MiniCard card={m.card} /></div>}
                      </div>
                    ))}
                    {typing && (
                      <div className="bg-white border border-zinc-200 rounded-2xl rounded-bl-sm px-4 py-3 w-fit shadow-sm flex gap-1">
                        {[0, 1, 2].map((i) => (
                          <motion.span key={i} className="w-2 h-2 rounded-full bg-emerald-600"
                            animate={{ opacity: [0.3, 1, 0.3], y: [0, -3, 0] }} transition={{ duration: 0.9, repeat: Infinity, delay: i * 0.15 }} />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="px-3 pt-2 flex gap-2 overflow-x-auto no-scrollbar bg-[#EFECE4]">
                  {GUEST_QUICK.map((q) => (
                    <button key={q.label} onClick={() => send(q.text)} disabled={typing}
                      data-testid={`guest-quick-${q.label.replace(/\s+/g, "-").toLowerCase()}`}
                      className="shrink-0 rounded-full border border-emerald-300 bg-white text-emerald-800 text-xs font-medium px-3 py-1.5 mb-2 hover:bg-emerald-50 transition-colors disabled:opacity-50">
                      {q.label}
                    </button>
                  ))}
                </div>
                <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="bg-[#EFECE4] px-3 py-3 flex items-center gap-2 shrink-0">
                  <input data-testid="guest-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Type a message…"
                    className="flex-1 rounded-full bg-white border border-zinc-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-emerald-500" />
                  <button type="submit" disabled={typing || !input.trim()} data-testid="guest-send"
                    className="w-11 h-11 rounded-full bg-emerald-900 hover:bg-emerald-800 text-white flex items-center justify-center shrink-0 disabled:opacity-40 transition-colors">
                    <PaperPlaneRight size={18} weight="fill" />
                  </button>
                </form>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
